/**
 * @fileoverview Composer service for the Teacher Assessment Drawer (AR-539).
 *
 * Sources:
 *   - `GET /api/arc_mosaico/session_meetings/:id/list_assessment_scores`
 *     (AR-538) — lesson + activities × {standards, skills} × students × score
 *   - `GET /api/arc_mosaico/task_track/deliverable`
 *     (AR-513) — submission status, files, late flag per activity × student
 *
 * The lesson endpoint marks `activity.has_deliverable` but does NOT include
 * submission details. We fan-out one `task_track/deliverable` call per
 * (activity-with-deliverable × visible-student) in parallel and merge the
 * results into `perStudent[].deliverable`. The 1+N is documented as a
 * backend gap to be merged into `list_assessment_scores` later.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import { fetchDeliverable } from '../../service/taskTrackerService'
import type { TaskTrackDeliverable } from '../../types'
import { buildItems, buildStudents, countUngeneratedItems } from './adapters'
import type { ListAssessmentScoresResponse } from './adapters'
import type {
  AssessmentItem,
  PerStudentAssessment,
  StudentDeliverableInfo,
  TeacherAssessmentDrawerData,
} from './types'

function adaptDeliverable(deliverable: TaskTrackDeliverable | null): StudentDeliverableInfo | undefined {
  if (!deliverable) return undefined
  const delivery = deliverable.delivery
  if (!delivery) {
    return { status: 'PENDING', submittedAt: null, late: false, files: [], links: [], googleDriveFiles: [] }
  }
  return {
    status: delivery.status === 'done' ? 'SUBMITTED' : 'PENDING',
    submittedAt: delivery.submittedAt || null,
    late: Boolean(delivery.late),
    files: delivery.files ?? [],
    links: delivery.links ?? [],
    googleDriveFiles: delivery.googleDriveFiles ?? [],
  }
}

interface FetchOptions {
  sessionMeetingId: string
  studentIds?: string[]
}

/**
 * Loads the full drawer payload. Returns the lesson, students, items, and
 * (best-effort) deliverable details merged per student.
 */
export async function fetchTeacherAssessmentDrawerData({
  sessionMeetingId,
  studentIds,
}: FetchOptions): Promise<TeacherAssessmentDrawerData> {
  const response = await api.get(
    ARC_MOSAICO_ENDPOINTS.sessionMeetingAssessments.listAssessmentScores(sessionMeetingId),
    { params: studentIds && studentIds.length > 0 ? { studentIds } : undefined },
  )
  const data = response.data as ListAssessmentScoresResponse

  const students = buildStudents(data.activities)
  const standards = buildItems(data.activities, 'standards')
  const skills = buildItems(data.activities, 'skills')
  const ungeneratedItemsCount = countUngeneratedItems(data.activities)

  // Fan-out deliverable details for activities that have a deliverable.
  const deliverableActivities = data.activities.filter(a => a.hasDeliverable)
  const deliverableActivitiesCount = deliverableActivities.length

  if (deliverableActivitiesCount > 0 && students.length > 0) {
    const pairs: Array<{ activityId: string; studentId: string }> = []
    for (const activity of deliverableActivities) {
      for (const student of students) {
        pairs.push({ activityId: activity.id, studentId: student.id })
      }
    }

    const results = await Promise.all(
      pairs.map(async pair => {
        try {
          const d = await fetchDeliverable(pair.activityId, pair.studentId)
          return { ...pair, deliverable: d }
        } catch {
          return { ...pair, deliverable: null as TaskTrackDeliverable | null }
        }
      }),
    )

    const byActivityAndStudent = new Map<string, StudentDeliverableInfo | undefined>()
    for (const r of results) {
      byActivityAndStudent.set(`${r.activityId}:${r.studentId}`, adaptDeliverable(r.deliverable))
    }

    const withDeliverables = (items: AssessmentItem[]): AssessmentItem[] =>
      items.map(item => {
        if (!item.activityHasDeliverable) return item
        const nextPerStudent: Record<string, PerStudentAssessment> = {}
        for (const [studentId, ps] of Object.entries(item.perStudent)) {
          const key = `${item.activityId}:${studentId}`
          const deliverable = byActivityAndStudent.get(key)
          nextPerStudent[studentId] = deliverable ? { ...ps, deliverable } : ps
        }
        return { ...item, perStudent: nextPerStudent }
      })

    return {
      project: data.project,
      lesson: data.sessionMeeting,
      students,
      standards: withDeliverables(standards),
      skills: withDeliverables(skills),
      deliverableActivitiesCount,
      ungeneratedItemsCount,
    }
  }

  return {
    project: data.project,
    lesson: data.sessionMeeting,
    students,
    standards,
    skills,
    deliverableActivitiesCount,
    ungeneratedItemsCount,
  }
}

interface UpdateScoreIndividualParams {
  sessionMeetingId: string
  assessmentScoreId: string
  masterScore?: number
  doNotAssess?: boolean
  justification?: string
}

interface UpdateScoreBulkParams {
  sessionMeetingId: string
  lv4ItemId: string
  studentIds?: string[]
  masterScore?: number
  doNotAssess?: boolean
  justification?: string
}

export interface UpdateAssessmentScoresResponse {
  updatedCount: number
  updatedIds: string[]
}

export async function updateAssessmentScoreIndividual(
  params: UpdateScoreIndividualParams,
): Promise<UpdateAssessmentScoresResponse> {
  const { sessionMeetingId, assessmentScoreId, masterScore, doNotAssess, justification } = params
  const body: Record<string, unknown> = { assessmentScoreId }
  if (masterScore !== undefined) body.masterScore = masterScore
  if (doNotAssess !== undefined) body.doNotAssess = doNotAssess
  if (justification !== undefined) body.justification = justification

  const response = await api.patch(
    ARC_MOSAICO_ENDPOINTS.sessionMeetingAssessments.updateAssessmentScores(sessionMeetingId),
    body,
  )
  return response.data as UpdateAssessmentScoresResponse
}

export async function updateAssessmentScoreBulk(
  params: UpdateScoreBulkParams,
): Promise<UpdateAssessmentScoresResponse> {
  const { sessionMeetingId, lv4ItemId, studentIds, masterScore, doNotAssess, justification } = params
  const body: Record<string, unknown> = { lv4ItemId }
  if (studentIds && studentIds.length > 0) body.studentIds = studentIds
  if (masterScore !== undefined) body.masterScore = masterScore
  if (doNotAssess !== undefined) body.doNotAssess = doNotAssess
  if (justification !== undefined) body.justification = justification

  const response = await api.patch(
    ARC_MOSAICO_ENDPOINTS.sessionMeetingAssessments.updateAssessmentScores(sessionMeetingId),
    body,
  )
  return response.data as UpdateAssessmentScoresResponse
}

/**
 * Journal fields of `GET /session_meetings/:id/student_assessment/:assessmentId`
 * (legacy teacher-authorized route, same one the legacy modal uses). Only the
 * fields consumed here are typed — they mirror `master_journal_id` /
 * `master_journal` from the backend's hash_student_assessment_info interactor.
 */
interface RawStudentAssessmentJournal {
  masterJournalId: string | null
  masterJournal: string | null
}

export interface LessonAssessmentJournal {
  journalId: string | null
  description: string
}

interface FetchLessonJournalParams {
  sessionMeetingId: string
  assessmentId: string
}

/** Loads the teacher note (master journal) for one student's lesson assessment. */
export async function fetchLessonAssessmentJournal({
  sessionMeetingId,
  assessmentId,
}: FetchLessonJournalParams): Promise<LessonAssessmentJournal> {
  const response = await api.get(
    `/session_meetings/${sessionMeetingId}/student_assessment/${assessmentId}`,
  )
  const raw = response.data as RawStudentAssessmentJournal
  return { journalId: raw.masterJournalId ?? null, description: raw.masterJournal ?? '' }
}

interface UpdateLessonJournalParams {
  studentId: string
  assessmentId: string
  journalId: string | null
  description: string
}

/**
 * Persists the teacher note through the legacy `assessment_scores_update_all`
 * endpoint with an empty scores payload — the exact route the teacher already
 * uses in the legacy flow. The backend 500s on a nil journal id, so bail out
 * when the assessment has no master journal yet (same guard as the student
 * drawer's `autosaveJournal`).
 */
export async function updateLessonAssessmentJournal({
  studentId,
  assessmentId,
  journalId,
  description,
}: UpdateLessonJournalParams): Promise<void> {
  if (!journalId) return
  await api.patch('/assessment_scores_update_all', {
    assessmentScores: [],
    studentId,
    assessmentId,
    journal: { id: journalId, description },
  })
}
